import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import StudentModel from "../../api/models/StudentModel";
import CandidateCourseModel from "../../api/models/CandidateCourseModel";
import { useQuery } from "react-query";
import { makeApiRequest } from "../../helpers";
import { ApiResponse } from "../../api/apiResponse";
import { StatisticsType } from "../../api/@types/scheduleTypes";
import { StudentProfile } from "../../components/student-info/StudentProfile";
import StudentSkills from "../../components/student-info/StudentSkills";
import StudentCard from "../../components/student-card";
import TabsComponent from "../../components/tabs/TabsComponent";
import {
  createSkills,
  indexCandidateSkills,
} from "../../api/endpoints/tutors/skills";
import { indexTutorStudentSchedules } from "../../api/endpoints/tutors/schedules";
import { SkillsParamsType } from "../../api/@types/globalTypes";
import { indexStudent } from "../../api/endpoints/tutors/students";
import TutorStudentNotes from "../../components/student-info/TutorStudentNotes";
import { sortAndCleanQuranProgress } from "../../helpers/statistics";
import { GetCurrentWindowSize } from "../../hooks/useWindowSize";
import FormattedScheduleResponseModel from "../../api/models/FormattedScheduleResponseModel";
import { FullSpinner } from "../../components/LoadingSpinner";

export default function TutorStudentProfile() {
  const currentWindow = GetCurrentWindowSize();
  const LocalBasePath = "page.supervisor.student";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [student, setStudent] = useState<StudentModel | null>(null);
  const { id } = useParams();
  const [sessionsNotes, setSessionsNotes] = useState<{
    passedNotes: number;
    unpassedNotes: number;
    sessionIds: string[];
  }>({ passedNotes: 0, unpassedNotes: 0, sessionIds: [] });
  const [courses, setCourses] = useState<CandidateCourseModel[]>([]);
  const [quranProgress, setQuranProgress] = useState<any>(null);
  const [sessionTardiness, setSessionTardiness] = useState([]);
  const [quranProgressSessions, setQuranProgressSessions] = useState<any>(null);
  const [reservedSchedule, setReservedSchedule] =
    useState<FormattedScheduleResponseModel | undefined>();
  const [quranPagesProgress, setQuranPagesProgress] = useState<{
    finished: number;
    remaining: number;
  }>({ finished: 0, remaining: 644 });
  const [skillsStatistics, setSkillsStatistics] = useState<{
    passed: number;
    total: number;
    unPassed: number;
  }>({ unPassed: 0, total: 0, passed: 0 });
  const [sessionsStatistics, setSessionsStatistics] = useState<{
    scheduled: number;
    completed: number;
    canceled: number;
  }>({
    completed: 0,
    scheduled: 0,
    canceled: 0,
  });
  const { data: finishedSkills, refetch: refetchSkills } = useQuery(
    ["skills", id],
    () => {
      const params: SkillsParamsType = {};
      params["student_id"] = id;
      return indexCandidateSkills(params);
    },
    {
      select: (response) => {
        let skills: string[] | undefined = undefined;
        if (response.isSuccess) {
          skills = response.data?.skills;
        }
        return skills;
      },
    }
  );

  const fetchSchedule = async () => {
    await makeApiRequest(
      () => indexTutorStudentSchedules(id!),
      (res: ApiResponse) => {
        const schedule = FormattedScheduleResponseModel.fromApiResponse(res);
        setReservedSchedule(schedule);
      }
    );
  };

  useEffect(() => {
    if (!!id) fetchSchedule();
  }, [id]);

  const getStudentInfo = () => {
    makeApiRequest(
      () => indexStudent(id ?? ""),
      (res: ApiResponse) => {
        const studentInfo = StudentModel.fromApiResponse(res.data?.student);
        setStudent(studentInfo);
        const { learn_dash_progresses }: StatisticsType = res.data;

        const {
          notes_statistics,
          quran_pages_statistics,
          session_status_statistics,
          session_tardiness,
          quran_progress,
          skills_statistics,
        }: StatisticsType = res.data.statistics;
        const ListCourses = CandidateCourseModel.fromApiResponse(
          learn_dash_progresses
        );
        //quranProgress start here
        const statisticsPages: number[] = [];
        const statisticsSessions: string[] = [];
        const sortedBySessionsQuranProgress: { [key: string]: number }[] =
          sortAndCleanQuranProgress(
            quran_progress,
            currentWindow === "sm" ? 5 : 10
          );
        sortedBySessionsQuranProgress.forEach((page: any) => {
          for (let key in page) {
            statisticsSessions.push(key);
            statisticsPages.push(+(page[key] ?? 0));
          }
        });
        setQuranProgress(statisticsPages);
        setQuranProgressSessions(statisticsSessions);
        setCourses(ListCourses);
        setSessionTardiness(session_tardiness);
        setQuranPagesProgress({
          finished: quran_pages_statistics.total_read_pages,
          remaining:
            quran_pages_statistics.total_pages -
            quran_pages_statistics.total_read_pages,
        });
        setSessionsStatistics({
          scheduled: session_status_statistics?.scheduled,
          completed: session_status_statistics?.completed,
          canceled: session_status_statistics?.canceled,
        });

        setSkillsStatistics({
          passed: skills_statistics?.passed_skills_count,
          total: skills_statistics?.total_skills_count,
          unPassed: skills_statistics?.total_skills_count,
        });
        setSessionsNotes({
          passedNotes: notes_statistics?.total_passed_notes_count,
          unpassedNotes: notes_statistics?.total_failed_notes_count,
          sessionIds: [...Array(notes_statistics.total_sessions_count)].map(
            (_, i) => `#${i + 1}`
          ),
        });
        setIsLoading(false);
      }
    );
  };
  const handleUpdateSkills = async (finishedSkills: string[]) => {
    const params: SkillsParamsType = {};
    if (!!id) {
      params["student_id"] = id!;
    }
    const response = await createSkills(params, finishedSkills);
    if (!!response.isSuccess) {
      refetchSkills();
      return true;
    } else {
      return false;
    }
  };
  const tabs = [
    {
      name: t("info"),
      content: (
        <StudentProfile
          quranPagesProgress={quranPagesProgress}
          quranProgress={quranProgress}
          quranProgressSessions={quranProgressSessions}
          courses={courses}
          student={student}
          sessionsNotes={sessionsNotes}
          sessionStatistics={sessionsStatistics}
          statistics={{
            session_tardiness: sessionTardiness,
          }}
          skillsStatistics={skillsStatistics}
          scheduleSlots={reservedSchedule?.schedule ?? {}}
        />
      ),
    },
    {
      name: t("skills"),
      content: (
        <div className="flex flex-col h-full">
          <StudentSkills
            handleUpdateSkills={handleUpdateSkills}
            isEditable={true}
            finishedSkills={finishedSkills || []}
          />
        </div>
      ),
    },
    {
      name: t("notes"),
      content: <TutorStudentNotes refetchUserData={getStudentInfo} />,
    },
  ];
  useEffect(() => {
    if (!!id) getStudentInfo();
  }, [id]);
  return (
    <>
      {isLoading ? (
        <div className="w-full h-full bg-white">
          <FullSpinner size={64} />
        </div>
      ) : (
        <div className="flex flex-col w-full min-h-full gap-8">
          <div className="px-6 pt-6">
            <StudentCard showKhetma={false} student={student} />
          </div>

          <TabsComponent
            isFlexCol={false}
            requests={true}
            defaultTab={+(searchParams.get("tab") ?? 0)}
            tabs={tabs}
          />
        </div>
      )}
    </>
  );
}
